// @ts-strict-ignore
import { FetchResult } from "@apollo/client";
import CegidInvoiceDialog from "@dashboard/cegid/CegidInvoiceDialog";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { MetadataIdSchema } from "@dashboard/components/Metadata";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import { DEFAULT_INITIAL_SEARCH_DATA } from "@dashboard/config";
import {
  CreateManualTransactionCaptureMutation,
  CreateManualTransactionCaptureMutationVariables,
  FulfillmentStatus,
  OrderCustomerNotificationTypeEnum,
  OrderFulfillmentApproveMutation,
  OrderFulfillmentApproveMutationVariables,
  OrderNoteUpdateMutation,
  OrderNoteUpdateMutationVariables,
  OrderPaymentReminderModeEnum,
  OrderTransactionRequestActionMutation,
  OrderTransactionRequestActionMutationVariables,
  OrderUpdateMutation,
  OrderUpdateMutationVariables,
  useCustomerAddressesQuery,
  useWarehouseListQuery,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import OrderCannotCancelOrderDialog from "@dashboard/orders/components/OrderCannotCancelOrderDialog";
import { OrderCustomerAddressesEditDialogOutput } from "@dashboard/orders/components/OrderCustomerAddressesEditDialog/types";
import OrderFulfillmentApproveDialog from "@dashboard/orders/components/OrderFulfillmentApproveDialog";
import OrderInvoiceEmailSendDialog from "@dashboard/orders/components/OrderInvoiceEmailSendDialog";
import { OrderManualTransactionDialog } from "@dashboard/orders/components/OrderManualTransactionDialog";
import { OrderMetadataDialog } from "@dashboard/orders/components/OrderMetadataDialog";
import { OrderRefundDialog } from "@dashboard/orders/components/OrderRefundDialog/OrderRefundDialog";
import { OrderTransactionActionDialog } from "@dashboard/orders/components/OrderTransactionActionDialog/OrderTransactionActionDialog";
import { isAnyAddressEditModalOpen } from "@dashboard/orders/utils/data";
import { OrderDiscountProvider } from "@dashboard/products/components/OrderDiscountProviders/OrderDiscountProvider";
import { OrderLineDiscountProvider } from "@dashboard/products/components/OrderDiscountProviders/OrderLineDiscountProvider";
import { useOrderVariantSearch } from "@dashboard/searches/useOrderVariantSearch";
import { PartialMutationProviderOutput } from "@dashboard/types";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { useState } from "react";
import { useIntl } from "react-intl";

import { customerUrl } from "../../../../customers/urls";
import {
  extractMutationErrors,
  getById,
  getMutationState,
  getStringOrPlaceholder,
} from "../../../../misc";
import { productUrl } from "../../../../products/urls";
import OrderAddressFields from "../../../components/OrderAddressFields/OrderAddressFields";
import OrderCancelDialog from "../../../components/OrderCancelDialog";
import OrderConfirmDialog from "../../../components/OrderConfirmDialog/OrderConfirmDialog";
import OrderDetailsPage from "../../../components/OrderDetailsPage";
import OrderFulfillmentCancelDialog from "../../../components/OrderFulfillmentCancelDialog";
import OrderFulfillmentTrackingDialog from "../../../components/OrderFulfillmentTrackingDialog";
import OrderMarkAsPaidDialog from "../../../components/OrderMarkAsPaidDialog/OrderMarkAsPaidDialog";
import OrderMarkAsReceivedDialog from "../../../components/OrderMarkAsReceivedDialog/OrderMarkAsReceivedDialog";
import OrderPaymentDialog from "../../../components/OrderPaymentDialog";
import OrderPaymentReminderDialog from "../../../components/OrderPaymentReminderDialog/OrderPaymentReminderDialog";
import OrderPaymentVoidDialog from "../../../components/OrderPaymentVoidDialog";
import OrderProductAddDialog from "../../../components/OrderProductAddDialog";
import OrderShippingMethodEditDialog from "../../../components/OrderShippingMethodEditDialog";
import {
  orderFulfillUrl,
  orderManualTransactionRefundUrl,
  orderPaymentRefundUrl,
  orderReturnUrl,
  orderTransactionRefundUrl,
  orderUrl,
  OrderUrlQueryParams,
} from "../../../urls";
import {
  getRecordedPontoRecolhaAddress,
  getResendableFulfillment,
  isParcelShopDelivery,
} from "../../../utils/delivery";
import { getOrderNif } from "../../../utils/nif";

interface OrderUnconfirmedDetailsProps {
  id: string;
  params: OrderUrlQueryParams;
  data: any;
  orderAddNote: any;
  orderUpdateNote: PartialMutationProviderOutput<
    OrderNoteUpdateMutation,
    OrderNoteUpdateMutationVariables
  >;
  orderLineUpdate: any;
  orderLineDelete: any;
  orderInvoiceRequest: any;
  onInvoiceMarkToggle: (marked: boolean) => void;
  onReceivedMarkToggle: (marked: boolean) => void;
  onSaveCancellationReason: (reason: string, notifyReason: boolean) => Promise<void> | void;
  onSendNotification: (
    notificationType: OrderCustomerNotificationTypeEnum,
    paymentReminderMode?: OrderPaymentReminderModeEnum,
  ) => void;
  handleSubmit: any;
  orderUpdate: PartialMutationProviderOutput<OrderUpdateMutation, OrderUpdateMutationVariables>;
  orderCancel: any;
  orderShippingMethodUpdate: any;
  orderLinesAdd: any;
  orderPaymentMarkAsPaid: any;
  orderVoid: any;
  orderPaymentCapture: any;
  orderFulfillmentApprove: PartialMutationProviderOutput<
    OrderFulfillmentApproveMutation,
    OrderFulfillmentApproveMutationVariables
  >;
  orderFulfillmentCancel: any;
  orderFulfillmentUpdateTracking: any;
  orderTransactionAction: PartialMutationProviderOutput<
    OrderTransactionRequestActionMutation,
    OrderTransactionRequestActionMutationVariables
  >;
  orderAddManualTransaction: PartialMutationProviderOutput<
    CreateManualTransactionCaptureMutation,
    CreateManualTransactionCaptureMutationVariables
  >;
  orderInvoiceSend: any;
  updateMetadataOpts: any;
  updatePrivateMetadataOpts: any;
  onBillingNifUpdate: (nif: string) => void;
  openModal: any;
  closeModal: any;
}

export const OrderUnconfirmedDetails = ({
  id,
  params,
  data,
  orderAddNote,
  orderUpdateNote,
  orderLineUpdate,
  orderLineDelete,
  onInvoiceMarkToggle,
  onReceivedMarkToggle,
  onSaveCancellationReason,
  onSendNotification,
  handleSubmit,
  orderUpdate,
  orderCancel,
  orderShippingMethodUpdate,
  orderLinesAdd,
  orderPaymentMarkAsPaid,
  orderVoid,
  orderPaymentCapture,
  orderFulfillmentApprove,
  orderFulfillmentCancel,
  orderFulfillmentUpdateTracking,
  orderInvoiceSend,
  updateMetadataOpts,
  updatePrivateMetadataOpts,
  orderTransactionAction,
  orderAddManualTransaction,
  onBillingNifUpdate,
  openModal,
  closeModal,
}: OrderUnconfirmedDetailsProps) => {
  const order = data.order;
  const shop = data.shop;
  const navigate = useNavigator();
  const {
    loadMore,
    search: variantSearch,
    result: variantSearchOpts,
  } = useOrderVariantSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      channel: order.channel.slug,
    },
  });
  const warehouses = useWarehouseListQuery({
    displayLoader: true,
    variables: {
      first: 30,
    },
  });
  const { data: customerAddresses, loading: customerAddressesLoading } = useCustomerAddressesQuery({
    variables: {
      id: order?.user?.id,
    },
    skip: !order?.user?.id || !isAnyAddressEditModalOpen(params.action),
  });
  const handleCustomerChangeAddresses = async (
    data: Partial<OrderCustomerAddressesEditDialogOutput>,
  ): Promise<FetchResult<OrderUpdateMutation>> =>
    orderUpdate.mutate({
      id,
      input: data,
    });
  const intl = useIntl();
  const [transactionReference, setTransactionReference] = useState("");
  const [markAsPaidNotifyCustomer, setMarkAsPaidNotifyCustomer] = useState(true);
  const [markReceivedNotifyCustomer, setMarkReceivedNotifyCustomer] = useState(true);
  const [markReceivedState, setMarkReceivedState] =
    useState<ConfirmButtonTransitionState>("default");
  const [paymentReminderMode, setPaymentReminderMode] = useState<OrderPaymentReminderModeEnum>(
    OrderPaymentReminderModeEnum.PAYMENT_LINK,
  );
  const [paymentReminderState, setPaymentReminderState] =
    useState<ConfirmButtonTransitionState>("default");
  const [confirmNotifyCustomer, setConfirmNotifyCustomer] = useState(true);
  const [confirmData, setConfirmData] = useState<MetadataIdSchema>(null);
  const [confirmButtonState, setConfirmButtonState] =
    useState<ConfirmButtonTransitionState>("default");
  const errors = orderUpdate.opts.data?.orderUpdate.errors || [];

  // Clicking "Confirm order" on the save bar opens a dialog with a "notify
  // customer" checkbox; the actual submit (order confirmation + metadata) runs
  // once the dialog is confirmed.
  const handleConfirmOpen = (data: MetadataIdSchema) => {
    setConfirmData(data);
    setConfirmButtonState("default");
    openModal("confirm-order");

    return Promise.resolve([]);
  };
  const handleConfirmOrder = async () => {
    setConfirmButtonState("loading");
    await handleSubmit(confirmData, confirmNotifyCustomer);
    setConfirmButtonState("success");
    closeModal();
  };

  const hasOrderFulfillmentsFulFilled = order?.fulfillments.some(
    fulfillment => fulfillment.status === FulfillmentStatus.FULFILLED,
  );

  // Marking received opens a dialog (to choose the optional email); undoing clears
  // the flag immediately. Resend is limited to the natively re-sendable invoice and
  // shipping emails.
  const handleReceivedMarkToggle = (marked: boolean) => {
    if (marked) {
      openModal("mark-received");

      return;
    }

    onReceivedMarkToggle(false);
  };
  const handleReceivedConfirm = async () => {
    setMarkReceivedState("loading");
    await onReceivedMarkToggle(true);

    if (markReceivedNotifyCustomer) {
      await onSendNotification(OrderCustomerNotificationTypeEnum.ORDER_RECEIVED);
    }

    setMarkReceivedState("success");
    closeModal();
  };
  const handleResendConfirmation = () =>
    onSendNotification(OrderCustomerNotificationTypeEnum.ORDER_CONFIRMATION);
  const handleResendPayment = () =>
    onSendNotification(OrderCustomerNotificationTypeEnum.PAYMENT_CONFIRMATION);
  const handleSendPaymentReminderConfirm = async () => {
    setPaymentReminderState("loading");
    await onSendNotification(
      OrderCustomerNotificationTypeEnum.PAYMENT_REMINDER,
      paymentReminderMode,
    );
    setPaymentReminderState("success");
    closeModal();
  };
  const latestInvoice = order?.invoices?.[order.invoices.length - 1];
  const trackedFulfillment = getResendableFulfillment(
    order?.fulfillments,
    isParcelShopDelivery(order?.deliveryType),
  );
  const handleResendInvoice = () => {
    if (latestInvoice) {
      orderInvoiceSend.mutate({ id: latestInvoice.id });
    }
  };
  const handleResendShipping = () => {
    if (!trackedFulfillment) {
      return;
    }

    // Pickup-point orders can't be re-sent until the shop address is recorded:
    // open the tracking dialog so staff can enter it, then it sends the email.
    if (
      isParcelShopDelivery(order?.deliveryType) &&
      !getRecordedPontoRecolhaAddress(order?.metadata)
    ) {
      navigate(orderUrl(id, { action: "edit-fulfillment", id: trackedFulfillment.id }));

      return;
    }

    orderFulfillmentUpdateTracking.mutate({
      id: trackedFulfillment.id,
      input: {
        trackingNumber: trackedFulfillment.trackingNumber,
        notifyCustomer: true,
      },
    });
  };

  return (
    <>
      <WindowTitle
        title={intl.formatMessage(
          {
            id: "GbBCmr",
            defaultMessage: "Order #{orderNumber}",
            description: "window title",
          },
          {
            orderNumber: getStringOrPlaceholder(order.number),
          },
        )}
      />
      <OrderDiscountProvider order={order}>
        <OrderLineDiscountProvider order={order}>
          <OrderDetailsPage
            onOrderReturn={() => navigate(orderReturnUrl(id))}
            loading={updateMetadataOpts.loading || updatePrivateMetadataOpts.loading}
            errors={errors}
            onNoteAdd={variables =>
              extractMutationErrors(
                orderAddNote.mutate({
                  input: variables,
                  order: id,
                }),
              )
            }
            onNoteUpdateLoading={orderUpdateNote.opts.loading}
            onNoteUpdate={(id, message) =>
              orderUpdateNote.mutate({
                order: id,
                input: {
                  message,
                },
              })
            }
            order={order}
            shop={shop}
            onTransactionAction={(id, action) =>
              openModal("transaction-action", {
                type: action,
                id,
                action: "transaction-action",
              })
            }
            onOrderLineAdd={() => openModal("add-order-line")}
            onOrderLineChange={(id, data) =>
              orderLineUpdate.mutate({
                id,
                input: data,
              })
            }
            onOrderLineRemove={id => orderLineDelete.mutate({ id })}
            onShippingMethodEdit={() => openModal("edit-shipping")}
            onShowMetadata={id => openModal("view-metadata", { id })}
            saveButtonBarState={getMutationState(
              updateMetadataOpts.called || updatePrivateMetadataOpts.called,
              updateMetadataOpts.loading || updatePrivateMetadataOpts.loading,
              [
                ...(updateMetadataOpts.data?.deleteMetadata.errors || []),
                ...(updateMetadataOpts.data?.updateMetadata.errors || []),
                ...(updatePrivateMetadataOpts.data?.deletePrivateMetadata.errors || []),
                ...(updatePrivateMetadataOpts.data?.updatePrivateMetadata.errors || []),
              ],
            )}
            shippingMethods={data?.order?.shippingMethods || []}
            onOrderCancel={() => openModal("cancel")}
            onOrderFulfill={() => navigate(orderFulfillUrl(id))}
            onFulfillmentApprove={fulfillmentId =>
              navigate(
                orderUrl(id, {
                  action: "approve-fulfillment",
                  id: fulfillmentId,
                }),
              )
            }
            onFulfillmentCancel={fulfillmentId =>
              navigate(
                orderUrl(id, {
                  action: "cancel-fulfillment",
                  id: fulfillmentId,
                }),
              )
            }
            onFulfillmentTrackingNumberUpdate={fulfillmentId =>
              navigate(
                orderUrl(id, {
                  action: "edit-fulfillment",
                  id: fulfillmentId,
                }),
              )
            }
            onPaymentCapture={() => openModal("capture")}
            onPaymentVoid={() => openModal("void")}
            onPaymentRefund={() => navigate(orderPaymentRefundUrl(id))}
            onProductClick={id => () => navigate(productUrl(id))}
            onBillingAddressEdit={() => openModal("edit-billing-address")}
            onShippingAddressEdit={() => openModal("edit-shipping-address")}
            onMarkAsPaid={() => openModal("mark-paid")}
            onConfirm={() => openModal("confirm-order")}
            onProfileView={() => navigate(customerUrl(order.user.id))}
            onAddManualTransaction={() => openModal("add-manual-transaction")}
            onInvoiceClick={id =>
              window.open(
                order.invoices.find(invoice => invoice.id === id)?.url,
                "_blank",
                "rel=noopener",
              )
            }
            onInvoiceGenerate={() => openModal("generate-cegid-invoice")}
            onInvoiceMarkToggle={onInvoiceMarkToggle}
            onReceivedMarkToggle={handleReceivedMarkToggle}
            onResendInvoice={handleResendInvoice}
            onResendShipping={handleResendShipping}
            onResendConfirmation={handleResendConfirmation}
            onResendPayment={handleResendPayment}
            onSendPaymentReminder={() => openModal("send-payment-reminder")}
            onInvoiceSend={id => openModal("invoice-send", { id })}
            onRefundAdd={() => openModal("add-refund")}
            onSubmit={handleConfirmOpen}
          />
        </OrderLineDiscountProvider>
      </OrderDiscountProvider>
      <CegidInvoiceDialog
        open={params.action === "generate-cegid-invoice"}
        orderId={id}
        onClose={closeModal}
        onCompleted={closeModal}
      />
      <OrderCannotCancelOrderDialog
        onClose={closeModal}
        open={params.action === "cancel" && hasOrderFulfillmentsFulFilled}
      />
      <OrderCancelDialog
        confirmButtonState={orderCancel.opts.status}
        errors={orderCancel.opts.data?.orderCancel.errors || []}
        number={order?.number}
        open={params.action === "cancel" && !hasOrderFulfillmentsFulFilled}
        onClose={closeModal}
        onSubmit={async ({ reason, notifyCustomer, notifyReason }) => {
          await onSaveCancellationReason(reason, notifyReason);

          if (reason) {
            await orderAddNote.mutate({
              order: id,
              input: {
                message: notifyReason
                  ? intl.formatMessage(
                      {
                        id: "YMV5lj",
                        defaultMessage: "Order cancelled. Reason: {reason}",
                        description: "order history note added on cancellation",
                      },
                      { reason },
                    )
                  : intl.formatMessage(
                      {
                        id: "DM42ko",
                        defaultMessage:
                          "Order cancelled. Reason: {reason} (not shared with customer)",
                        description:
                          "order history note added on cancellation, reason kept internal",
                      },
                      { reason },
                    ),
              },
            });
          }

          orderCancel.mutate({ id, notifyCustomer });
        }}
      />
      <OrderShippingMethodEditDialog
        confirmButtonState={orderShippingMethodUpdate.opts.status}
        errors={orderShippingMethodUpdate.opts.data?.orderUpdateShipping.errors || []}
        open={params.action === "edit-shipping"}
        shippingMethod={order?.shippingMethod?.id}
        shippingMethods={order?.shippingMethods}
        onClose={closeModal}
        onSubmit={variables =>
          extractMutationErrors(
            orderShippingMethodUpdate.mutate({
              id,
              input: {
                shippingMethod: variables.shippingMethod,
              },
            }),
          )
        }
      />
      <OrderProductAddDialog
        confirmButtonState={orderLinesAdd.opts.status}
        errors={orderLinesAdd.opts.data?.orderLinesCreate.errors || []}
        loading={variantSearchOpts.loading}
        open={params.action === "add-order-line"}
        hasMore={variantSearchOpts.data?.search?.pageInfo?.hasNextPage}
        products={mapEdgesToItems(variantSearchOpts?.data?.search)}
        onClose={closeModal}
        onFetch={variantSearch}
        onFetchMore={loadMore}
        channelName={order.channel?.name}
        onSubmit={variants =>
          orderLinesAdd.mutate({
            id,
            input: variants.map(variant => ({
              quantity: 1,
              variantId: variant.id,
            })),
          })
        }
      />
      <OrderConfirmDialog
        confirmButtonState={confirmButtonState}
        open={params.action === "confirm-order"}
        orderNumber={getStringOrPlaceholder(order?.number)}
        notifyCustomer={confirmNotifyCustomer}
        onClose={closeModal}
        onConfirm={handleConfirmOrder}
        handleNotifyCustomer={setConfirmNotifyCustomer}
      />
      <OrderMarkAsReceivedDialog
        confirmButtonState={markReceivedState}
        open={params.action === "mark-received"}
        notifyCustomer={markReceivedNotifyCustomer}
        onClose={closeModal}
        onConfirm={handleReceivedConfirm}
        handleNotifyCustomer={setMarkReceivedNotifyCustomer}
      />
      <OrderPaymentReminderDialog
        confirmButtonState={paymentReminderState}
        errors={[]}
        open={params.action === "send-payment-reminder"}
        mode={paymentReminderMode}
        onClose={closeModal}
        onConfirm={handleSendPaymentReminderConfirm}
        onModeChange={setPaymentReminderMode}
      />
      <OrderMarkAsPaidDialog
        confirmButtonState={orderPaymentMarkAsPaid.opts.status}
        errors={orderPaymentMarkAsPaid.opts.data?.orderMarkAsPaid.errors || []}
        onClose={closeModal}
        onConfirm={() =>
          orderPaymentMarkAsPaid.mutate({
            id,
            transactionReference,
            notifyCustomer: markAsPaidNotifyCustomer,
          })
        }
        open={params.action === "mark-paid"}
        transactionReference={transactionReference}
        handleTransactionReference={({ target }) => setTransactionReference(target.value)}
        notifyCustomer={markAsPaidNotifyCustomer}
        handleNotifyCustomer={setMarkAsPaidNotifyCustomer}
      />
      <OrderTransactionActionDialog
        confirmButtonState={orderTransactionAction.opts.status}
        onClose={closeModal}
        open={params.action === "transaction-action"}
        action={params.type}
        onSubmit={() =>
          orderTransactionAction
            .mutate({
              action: params.type,
              transactionId: params.id,
            })
            .finally(() => closeModal())
        }
      />
      <OrderMetadataDialog
        open={params.action === "view-metadata"}
        onClose={closeModal}
        lineId={params.id}
        orderId={id}
      />

      <OrderPaymentVoidDialog
        confirmButtonState={orderVoid.opts.status}
        errors={orderVoid.opts.data?.orderVoid.errors || []}
        open={params.action === "void"}
        onClose={closeModal}
        onConfirm={() => orderVoid.mutate({ id })}
      />
      <OrderPaymentDialog
        confirmButtonState={orderPaymentCapture.opts.status}
        errors={orderPaymentCapture.opts.data?.orderCapture.errors || []}
        initial={order?.total.gross.amount}
        open={params.action === "capture"}
        onClose={closeModal}
        onSubmit={variables =>
          orderPaymentCapture.mutate({
            ...variables,
            id,
          })
        }
      />
      <OrderFulfillmentApproveDialog
        confirmButtonState={orderFulfillmentApprove.opts.status}
        errors={orderFulfillmentApprove.opts.data?.orderFulfillmentApprove.errors || []}
        open={params.action === "approve-fulfillment"}
        onConfirm={({ notifyCustomer }) =>
          orderFulfillmentApprove.mutate({
            id: params.id,
            notifyCustomer,
          })
        }
        onClose={closeModal}
      />
      <OrderFulfillmentCancelDialog
        confirmButtonState={orderFulfillmentCancel.opts.status}
        errors={orderFulfillmentCancel.opts.data?.orderFulfillmentCancel.errors || []}
        open={params.action === "cancel-fulfillment"}
        warehouses={mapEdgesToItems(warehouses?.data?.warehouses)}
        fulfillmentStatus={order?.fulfillments.find(getById(params.id))?.status}
        onConfirm={variables =>
          orderFulfillmentCancel.mutate({
            id: params.id,
            input: variables,
          })
        }
        onClose={closeModal}
      />
      <OrderFulfillmentTrackingDialog
        confirmButtonState={orderFulfillmentUpdateTracking.opts.status}
        errors={
          orderFulfillmentUpdateTracking.opts.data?.orderFulfillmentUpdateTracking.errors || []
        }
        open={params.action === "edit-fulfillment"}
        trackingNumber={
          data?.order?.fulfillments.find(fulfillment => fulfillment.id === params.id)
            ?.trackingNumber
        }
        isParcelShop={isParcelShopDelivery(order?.deliveryType)}
        pontoRecolhaAddress={getRecordedPontoRecolhaAddress(order?.metadata)}
        onConfirm={variables =>
          orderFulfillmentUpdateTracking.mutate({
            id: params.id,
            input: variables,
          })
        }
        onClose={closeModal}
      />
      <OrderInvoiceEmailSendDialog
        confirmButtonState={orderInvoiceSend.opts.status}
        errors={orderInvoiceSend.opts.data?.invoiceSendEmail.errors || []}
        open={params.action === "invoice-send"}
        invoice={order?.invoices?.find(invoice => invoice.id === params.id)}
        onClose={closeModal}
        onSend={() => orderInvoiceSend.mutate({ id: params.id })}
      />
      <OrderManualTransactionDialog
        dialogProps={{
          open: params.action === "add-manual-transaction",
          onClose: closeModal,
        }}
        submitState={orderAddManualTransaction.opts.status}
        error={
          orderAddManualTransaction.opts?.error?.message ||
          orderAddManualTransaction.opts?.data?.transactionCreate?.errors?.[0]?.message
        }
        currency={data?.order?.totalBalance?.currency}
        onAddTransaction={({ amount, description }) =>
          orderAddManualTransaction.mutate({
            currency: data?.order?.totalBalance?.currency,
            orderId: id,
            amount,
            description,
          })
        }
      />
      <OrderAddressFields
        action={params?.action}
        customerAddressesLoading={customerAddressesLoading}
        orderShippingAddress={order?.shippingAddress}
        orderBillingAddress={order?.billingAddress}
        isDraft={false}
        countries={data?.shop?.countries}
        customer={customerAddresses?.user}
        orderNif={getOrderNif(order)}
        onNifSubmit={onBillingNifUpdate}
        onClose={closeModal}
        onConfirm={handleCustomerChangeAddresses}
        confirmButtonState={orderUpdate.opts.status}
        errors={orderUpdate.opts.data?.orderUpdate.errors}
      />

      <OrderRefundDialog
        open={params.action === "add-refund"}
        onClose={closeModal}
        onStandardRefund={() => navigate(orderTransactionRefundUrl(id), { replace: true })}
        onManualRefund={() => navigate(orderManualTransactionRefundUrl(id), { replace: true })}
      />
    </>
  );
};
